import { FC, useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { Listing, Sale, InventoryItem } from "../../lib/types"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Card,
  CardContent,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel
} from "../../components/ui"
import {
  DollarSign,
  Package,
  ShoppingCart,
  AlertCircle,
  CheckCircle2,
  TrendingUp
} from "lucide-react"
import { useListingContext } from "../../hooks/use-listings"
import { useInventoryContext } from "../../hooks/use-inventory"
import { Timestamp } from "firebase/firestore"

type RecordSaleDialogProps = {
  isShowing: boolean
  close: () => void
  listing: Listing | null
}

export const RecordSaleDialog: FC<RecordSaleDialogProps> = ({
  isShowing,
  close,
  listing
}) => {
  const { register, getValues, setValue, reset, watch } = useForm()
  const { addSale } = useListingContext()
  const { inventory } = useInventoryContext()

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [inventoryItem, setInventoryItem] = useState<InventoryItem | null>(null)

  const quantitySold = watch("quantitySold")
  const salePrice = watch("salePrice")

  useEffect(() => {
    if (listing && isShowing) {
      // Find the inventory item
      const invItem = inventory.find(item => item.id === listing.invSourceId)
      setInventoryItem(invItem || null)

      // Reset form with listing defaults
      reset({
        quantitySold: 1,
        salePrice: Number(listing.price) || 0,
        platform: listing.platform || "",
        buyerInfo: "",
        notes: ""
      })
    }
  }, [listing, isShowing, inventory])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}
    const qty = Number(getValues("quantitySold"))
    const price = Number(getValues("salePrice"))

    if (!qty || qty < 1) {
      newErrors.quantitySold = "Quantity must be at least 1"
    }

    if (listing && qty > listing.quantityAvailable) {
      newErrors.quantitySold = `Only ${listing.quantityAvailable} available`
    }

    if (!inventoryItem || qty > inventoryItem.quantity) {
      newErrors.quantitySold = `Not enough inventory (${inventoryItem?.quantity || 0} in stock)`
    }

    if (!price || price < 0) {
      newErrors.salePrice = "Valid price required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleRecordSale = async () => {
    if (!validateForm() || !listing || !inventoryItem) return

    const formData = getValues()
    const qty = Number(formData.quantitySold)
    const price = Number(formData.salePrice)

    const newSale: Sale = {
      createdAt: Timestamp.now(),
      listingId: listing.id,
      invSourceId: listing.invSourceId,
      quantitySold: qty,
      salePrice: price,
      totalAmount: qty * price,
      platform: formData.platform || listing.platform || "",
      ...(formData.buyerInfo && { buyerInfo: formData.buyerInfo }),
      ...(formData.notes && { notes: formData.notes })
    }

    await addSale(newSale, inventoryItem)
    close()
    reset()
  }

  if (!listing) return null

  const totalAmount = (Number(quantitySold) || 0) * (Number(salePrice) || 0)

  return (
    <Dialog open={isShowing} onOpenChange={close}>
      <DialogContent className="max-w-xl dark:bg-neutral800 dark:border-neutral700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-neutral100">
            <ShoppingCart className="text-success500" width={24} height={24} />
            Record Sale
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Listing Info Banner */}
          <Card className="bg-sumoCream/30 dark:bg-sumoPrimary/10 border-sumoPrimary/30 dark:border-sumoPrimary/40">
            <CardContent className="pt-4">
              <div className="space-y-2">
                <h4 className="font-semibold text-neutral900 dark:text-neutral100">{listing.title}</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-neutral600 dark:text-neutral400">Available:</span>
                    <span className="ml-2 font-semibold text-neutral900 dark:text-neutral100">
                      {listing.quantityAvailable} units
                    </span>
                  </div>
                  <div>
                    <span className="text-neutral600 dark:text-neutral400">List Price:</span>
                    <span className="ml-2 font-semibold text-neutral900 dark:text-neutral100">
                      ${(Number(listing.price) || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sale Details Form */}
          <div className="space-y-4">
            {/* Quantity and Price */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-neutral700 dark:text-neutral300">
                  <Package width={16} height={16} className="text-success500" />
                  Quantity Sold *
                </label>
                <Input
                  type="number"
                  min="1"
                  max={listing.quantityAvailable}
                  placeholder="1"
                  {...register("quantitySold", { required: true })}
                  className={errors.quantitySold ? 'border-error500' : ''}
                />
                {errors.quantitySold && (
                  <p className="text-xs text-error500 flex items-center gap-1">
                    <AlertCircle width={12} /> {errors.quantitySold}
                  </p>
                )}
                <p className="text-xs text-neutral500 dark:text-neutral400">
                  Max: {listing.quantityAvailable} | Inventory: {inventoryItem?.quantity || 0}
                </p>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-neutral700 dark:text-neutral300">
                  <DollarSign width={16} height={16} className="text-success500" />
                  Sale Price *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  {...register("salePrice", { required: true })}
                  className={errors.salePrice ? 'border-error500' : ''}
                />
                {errors.salePrice && (
                  <p className="text-xs text-error500 flex items-center gap-1">
                    <AlertCircle width={12} /> {errors.salePrice}
                  </p>
                )}
                <p className="text-xs text-neutral500 dark:text-neutral400">Price per unit</p>
              </div>
            </div>

            {/* Total Amount Display */}
            {totalAmount > 0 && (
              <Card className="bg-success50 dark:bg-success900/20 border-success500 dark:border-success700">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm font-medium text-success700">
                      <TrendingUp width={16} height={16} />
                      Total Sale Amount
                    </span>
                    <span className="text-2xl font-bold text-success700">
                      ${totalAmount.toFixed(2)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Platform */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-neutral700 dark:text-neutral300">
                Platform
                <span className="text-neutral400 dark:text-neutral500 font-normal text-xs ml-2">(Optional)</span>
              </label>
              <Select
                value={getValues("platform")}
                onValueChange={(value) => setValue("platform", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Sales Platforms</SelectLabel>
                    <SelectItem value="eBay">eBay</SelectItem>
                    <SelectItem value="Facebook Marketplace">Facebook Marketplace</SelectItem>
                    <SelectItem value="Craigslist">Craigslist</SelectItem>
                    <SelectItem value="Amazon">Amazon</SelectItem>
                    <SelectItem value="Etsy">Etsy</SelectItem>
                    <SelectItem value="Shopify">Shopify</SelectItem>
                    <SelectItem value="In Person">In Person</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {/* Buyer Info */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-neutral700 dark:text-neutral300">
                Buyer Information
                <span className="text-neutral400 dark:text-neutral500 font-normal text-xs ml-2">(Optional)</span>
              </label>
              <Input
                type="text"
                placeholder="e.g., Username, Name, Email"
                {...register("buyerInfo")}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-neutral700 dark:text-neutral300">
                Notes
                <span className="text-neutral400 dark:text-neutral500 font-normal text-xs ml-2">(Optional)</span>
              </label>
              <Input
                type="text"
                placeholder="Any additional notes about this sale"
                {...register("notes")}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleRecordSale}
            className="flex items-center gap-2"
          >
            <CheckCircle2 width={16} height={16} />
            Record Sale
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
